import axios from 'axios';
import { isNative, platform } from '../platform/env';
import { secureGet, secureSet, secureRemove } from '../platform/secureStore';
import { storageRemove } from '../platform/storage';
import { getNetworkStatus } from '../platform/network';
import { getNativeApiOrigin } from '../platform/apiOrigin';
import { showOfflineToast } from '../services/alerts.service';
import { isTokenExpired } from '../Utilities/jwt';

// Only fall back to localhost when the var is unset (dev with no .env). An
// explicit empty value (production/stage) means "same-origin / relative" on
// web; on native there is no same-origin backend, so the origin comes from
// platform/apiOrigin.js (spec 000211 Q6).
const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL;
const resolveBaseUrl = () => {
    if (isNative) return getNativeApiOrigin();
    return configuredBaseUrl == null ? 'https://localhost:5011' : configuredBaseUrl;
};
const baseURLValue = resolveBaseUrl().replace(/\/$/, '');

const bearerOf = (headers) => {
    const value = headers?.Authorization || headers?.authorization || '';
    return value.startsWith('Bearer ') ? value.slice(7) : null;
};

// spec 000211 Q4: everything below is native-only. The web app keeps the exact
// axios behavior it had before the native layer (no timeout, no extra header,
// no interceptors).
let installed = false;
function installNativeInterceptors() {
    if (installed || !isNative) return;
    installed = true;

    axios.defaults.timeout = 30000;
    axios.defaults.headers.common['X-Client'] = `mobile-${platform}`;

    // Request: attach token from secure store if not already set (cold start /
    // token rotation handled transparently). Existing setToken() flow also
    // sets the default header so this is mostly a safety net for native cold
    // starts before the autoLogin flow has populated defaults.
    axios.interceptors.request.use(async (config) => {
        if (!config.headers.Authorization && !config.headers.authorization) {
            const token = await secureGet('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    });

    // Response: network failure -> single offline toast; 401 -> force logout,
    // but only when the session is really gone (spec 000211 Q5). The Mobile BFF
    // answers 401 for ANY exception in several controllers, so a 401 with a
    // still-valid token is a server error, not an auth failure - reject the call
    // and keep the user signed in.
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            if (!error.response) {
                // No HTTP response: timeout / DNS fail / offline
                try {
                    const status = await getNetworkStatus();
                    if (!status.connected) {
                        showOfflineToast();
                    }
                } catch {
                    // best-effort
                }
            } else if (error.response.status === 401) {
                const token = bearerOf(error.config?.headers) || await secureGet('authToken');
                if (!token || isTokenExpired(token)) {
                    try {
                        await secureRemove('authToken');
                        await storageRemove('isLoggedIn');
                    } finally {
                        delete axios.defaults.headers.common['Authorization'];
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('auth:forceLogout'));
                        }
                    }
                }
            }
            return Promise.reject(error);
        }
    );
}

installNativeInterceptors();

const setToken = (token) => {
    if (token) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
        secureSet('authToken', token).catch(() => {});
    } else {
        delete axios.defaults.headers.common['Authorization'];
        secureRemove('authToken').catch(() => {});
    }
};

const baseURL = () => {
    axios.defaults.baseURL = baseURLValue;
};

export { baseURL };
export default setToken;
